import { format } from "date-fns";
import { queryCache } from "react-query";
import { WSMutationConfig, useWSMutation } from "../../../../query/helpers";
import { useUserId } from "../../../../query/hooks/helpers";
import { QUERY_CUSTOMER_ENTITY } from "../../../../query/onboarding/queries/useQueryCustomerEntity";
import { QUERY_VERIFICATION_MISSING_DATA } from "../../../../query/onboarding/queries/useQueryVerificationMissingFields";
import { QUERY_VERIFICATIONS } from "../../../../query/onboarding/queries/useQueryVerifications";
import { QUERY_USERS_ACTIVITIES } from "../../../../query/users/keys";
import { useUserProfile } from "../../../../query/users/queries";
import {
  createCustomerEntity,
  getCustomerEntity,
  updateCustomerData
} from "../../../../services/api/onboarding/customer";
import { performVerification } from "../../../../services/api/onboarding/verifications";
import { usersService } from "../../../../services/users";
import { WSServiceError } from "../../../../utils/serviceHelper";
import { FormData } from "../../types";

export const useMutationSubmit = (
  config?: WSMutationConfig<FormData, WSServiceError, FormData>
) => {
  const userId = useUserId();
  const queryUser = useUserProfile(userId);

  return useWSMutation<FormData, WSServiceError, FormData>(
    async data => {
      if (!data.type || !data.country) {
        throw new Error("Invalid form data");
      }

      try {
        // Check if there is customer entity
        await getCustomerEntity();
      } catch (error) {
        // Create it if it doesn't exist
        await createCustomerEntity({ country: data.country, type: data.type });
      }

      // Update customer data
      if (data.type === "Business") {
        await updateCustomerData("Entity", {
          customerData: {
            phoneNumber: queryUser.data?.phone.number,
            numberOfEmployees: data.numberOfEmployees,
            legalBusinessName: data.business.legalBusinessName,
            doingBusinessAs: data.business.dba || undefined,
            federalTaxClassification: data.business.taxClassification,
            businessTaxId: data.business.taxId,
            industry: data.business.industry,
            website: data.business.noWebsite ? "None" : data.business.website,

            addressLine1: data.business.address.addressLine1,
            city: data.business.address.city,
            region: data.business.address.state,
            postalCode: data.business.address.postalCode,
            country: data.business.address.country,

            regionOfFormation: data.business.stateOfIncorporation
          }
        });

        await updateCustomerData("Representative", {
          customerData: {
            firstName: data.accountHolder.firstName,
            lastName: data.accountHolder.lastName,
            occupation: data.accountHolder.jobTitle,
            dateOfBirth: data.accountHolder.birthday
              ? format(data.accountHolder.birthday, "yyyy-MM-dd")
              : undefined,
            country: data.accountHolder.address.country,
            individualTaxId: data.accountHolder.taxId,
            addressLine1: data.accountHolder.address.addressLine1,
            city: data.accountHolder.address.city,
            region: data.accountHolder.address.state,
            postalCode: data.accountHolder.address.postalCode
          },
          ownershipStake: data.accountHolder.ownership.stake ?? 0
        });
      } else {
        await updateCustomerData("Entity", {
          customerData: {
            email: queryUser.data?.email,
            phoneNumber: queryUser.data?.phone.number,
            firstName: data.accountHolder.firstName,
            lastName: data.accountHolder.lastName,
            occupation: data.accountHolder.jobTitle,
            dateOfBirth: data.accountHolder.birthday
              ? format(data.accountHolder.birthday, "yyyy-MM-dd")
              : undefined,
            country: data.accountHolder.address.country,
            individualTaxId: data.accountHolder.taxId,
            addressLine1: data.accountHolder.address.addressLine1,
            city: data.accountHolder.address.city,
            region: data.accountHolder.address.state,
            postalCode: data.accountHolder.address.postalCode
          }
        });
      }

      // Perform verification
      if (data.type === "Business") {
        await performVerification("Tax");
      } else {
        await performVerification("Banking");
      }

      // Update activity
      await usersService.activity.update(userId, {
        flows: {
          paymentsSetup: {
            currentStep: 3,
            version: 3,
            complete: false,
            totalSteps: 5
          }
        }
      });

      await queryCache.refetchQueries(QUERY_USERS_ACTIVITIES);

      return data;
    },
    {
      dependencies: [
        QUERY_CUSTOMER_ENTITY,
        QUERY_VERIFICATIONS,
        QUERY_VERIFICATION_MISSING_DATA,
        QUERY_USERS_ACTIVITIES
      ],
      ...config
    }
  );
};
